import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkle, Pencil, Binoculars, MagicWand, ArrowLeft, Check,
  CaretRight, ListNumbers, Eye, ArrowsClockwise, Trash, FloppyDisk,
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const C = {
  bg: '#0a0a0a', surface: '#141414', surface2: '#1a1a1a',
  border: 'rgba(255,255,255,0.06)', borderHi: 'rgba(255,255,255,0.12)',
  borderLime: 'rgba(200,241,53,0.32)',
  lime: '#c8f135', limeText: '#04050a',
  text: '#f0f0e8', dim: 'rgba(240,240,232,0.65)', faint: 'rgba(240,240,232,0.35)',
};
const MONO = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, monospace';
const DISP = "'Syne', system-ui, sans-serif";

/* ─────────────────────────────────────────────────────────────────
   ScalitOS Blog Post Creator
   Three-screen flow: topic → generation → output.
   No "AI" language anywhere. Reads like a service, not a tool.
   ───────────────────────────────────────────────────────────────── */

/* Reusable card primitive */
const Card = ({ children, hover, onClick, recommended, testid, ...rest }) => (
  <div
    onClick={onClick}
    data-testid={testid}
    style={{
      background: C.surface, border: `1px solid ${recommended ? C.borderLime : C.border}`,
      borderRadius: 16, padding: 'clamp(22px, 3vw, 32px)',
      cursor: onClick ? 'pointer' : 'default', position: 'relative',
      transition: 'border-color 0.2s, background 0.2s, transform 0.2s',
      ...(hover && onClick ? { ':hover': { borderColor: C.borderHi } } : {}),
    }}
    onMouseEnter={(e) => { if (onClick) { e.currentTarget.style.borderColor = C.borderLime; e.currentTarget.style.background = '#181818'; } }}
    onMouseLeave={(e) => { if (onClick) { e.currentTarget.style.borderColor = recommended ? C.borderLime : C.border; e.currentTarget.style.background = C.surface; } }}
    {...rest}
  >
    {recommended && (
      <div style={{
        position: 'absolute', top: 14, right: 16,
        background: C.lime, color: C.limeText,
        padding: '3px 8px', borderRadius: 4,
        fontFamily: MONO, fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase',
      }}>Recommended</div>
    )}
    {children}
  </div>
);

/* Eyebrow / kicker label */
const Kicker = ({ children, lime }) => (
  <p style={{
    fontFamily: MONO, fontSize: 10, letterSpacing: '0.22em',
    color: lime ? C.lime : C.faint, textTransform: 'uppercase',
    fontWeight: 700, marginBottom: 10,
  }}>{children}</p>
);

/* ───────── Screen 1 — Topic selection ───────── */

const TopicSelector = ({ onPickModeAuto, onPickModeTitle, onPickModeCompetitor }) => (
  <div data-testid="blog-topic-selector">
    <div style={{ textAlign: 'center', marginBottom: 'clamp(36px, 5vw, 56px)' }}>
      <Kicker lime>Blog post · 3 credits</Kicker>
      <h1 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(30px, 5vw, 52px)', letterSpacing: '-0.035em', color: C.text, lineHeight: 1.05, marginBottom: 14 }}>
        What should this post be about?
      </h1>
      <p style={{ fontFamily: MONO, fontSize: 14, color: C.dim, lineHeight: 1.8, maxWidth: 560, margin: '0 auto' }}>
        We&apos;ll handle the research, writing, and optimisation.
      </p>
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: 18 }}>
      <Card recommended hover onClick={onPickModeAuto} testid="blog-mode-auto">
        <MagicWand size={28} weight="duotone" color={C.lime} />
        <h3 style={{ fontFamily: DISP, fontWeight: 700, fontSize: 20, color: C.text, letterSpacing: '-0.01em', marginTop: 18, marginBottom: 8 }}>Write it for me</h3>
        <p style={{ fontFamily: MONO, fontSize: 13, lineHeight: 1.75, color: C.dim }}>
          We&apos;ll pick the best topic for your business right now based on what your audience is searching for.
        </p>
      </Card>
      <Card hover onClick={onPickModeTitle} testid="blog-mode-title">
        <Pencil size={28} weight="duotone" color={C.lime} />
        <h3 style={{ fontFamily: DISP, fontWeight: 700, fontSize: 20, color: C.text, letterSpacing: '-0.01em', marginTop: 18, marginBottom: 8 }}>I have a topic</h3>
        <p style={{ fontFamily: MONO, fontSize: 13, lineHeight: 1.75, color: C.dim }}>
          Tell us what you want to write about and we&apos;ll handle the rest.
        </p>
      </Card>
      <Card hover onClick={onPickModeCompetitor} testid="blog-mode-competitor">
        <Binoculars size={28} weight="duotone" color={C.lime} />
        <h3 style={{ fontFamily: DISP, fontWeight: 700, fontSize: 20, color: C.text, letterSpacing: '-0.01em', marginTop: 18, marginBottom: 8 }}>Beat a competitor</h3>
        <p style={{ fontFamily: MONO, fontSize: 13, lineHeight: 1.75, color: C.dim }}>
          Find a topic your competitors rank for — and write a better version.
        </p>
      </Card>
    </div>
  </div>
);

/* ───────── Topic confirmation tile (used by Auto + Competitor modes) ───────── */

const TopicConfirm = ({ topic, keyword, volume, why, alternates, onConfirm, onPickAlternate, onTryAgain }) => (
  <div data-testid="blog-topic-confirm">
    <Kicker lime>Suggested topic</Kicker>
    <div style={{ background: C.surface, border: `1px solid ${C.borderLime}`, borderRadius: 16, padding: 'clamp(26px, 4vw, 38px)', marginBottom: 24 }}>
      <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(22px, 3vw, 30px)', letterSpacing: '-0.02em', color: C.text, lineHeight: 1.15, marginBottom: 18 }}>{topic}</h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 14, marginBottom: 22 }}>
        <div>
          <p style={{ fontFamily: MONO, fontSize: 9, color: C.faint, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 5 }}>Target keyword</p>
          <p style={{ fontFamily: MONO, fontSize: 13, color: C.text, fontWeight: 700 }}>{keyword}</p>
        </div>
        <div>
          <p style={{ fontFamily: MONO, fontSize: 9, color: C.faint, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 5 }}>Estimated search volume</p>
          <p style={{ fontFamily: MONO, fontSize: 13, color: C.text, fontWeight: 700, textTransform: 'capitalize' }}>{volume || 'medium'}</p>
        </div>
      </div>

      {why && (
        <p style={{ fontFamily: MONO, fontSize: 12, color: C.dim, lineHeight: 1.75, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
          {why}
        </p>
      )}

      <div style={{ display: 'flex', gap: 12, marginTop: 24, flexWrap: 'wrap' }}>
        <button onClick={onConfirm} data-testid="blog-topic-confirm-btn"
          style={{ background: C.lime, color: C.limeText, border: 'none', cursor: 'pointer',
            padding: '12px 22px', borderRadius: 10,
            fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
          }}>
          <Check size={13} weight="bold" style={{ display: 'inline-block', marginRight: 6, verticalAlign: '-2px' }} />
          Use this topic
        </button>
        {onTryAgain && (
          <button onClick={onTryAgain} data-testid="blog-topic-try-again-btn"
            style={{ background: 'transparent', color: C.text, border: `1px solid ${C.borderHi}`, cursor: 'pointer',
              padding: '12px 22px', borderRadius: 10,
              fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
            }}>
            Try a different topic &rarr;
          </button>
        )}
      </div>
    </div>

    {alternates && alternates.length > 0 && (
      <>
        <Kicker>Or pick an alternate</Kicker>
        <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
          {alternates.map((alt, i) => (
            <button key={i} onClick={() => onPickAlternate(alt)} data-testid={`blog-topic-alt-${i}`}
              style={{ textAlign: 'left', background: C.surface, border: `1px solid ${C.border}`,
                borderRadius: 12, padding: '14px 18px', cursor: 'pointer',
                transition: 'border-color 0.2s, background 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.borderLime; e.currentTarget.style.background = '#181818'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.surface; }}>
              <p style={{ fontFamily: DISP, fontWeight: 700, fontSize: 15, color: C.text, marginBottom: 4 }}>{alt.topic}</p>
              <p style={{ fontFamily: MONO, fontSize: 11, color: C.dim }}>
                Target: <span style={{ color: C.text }}>{alt.keyword}</span> · {alt.search_volume || 'medium'} search volume
              </p>
            </button>
          ))}
        </div>
      </>
    )}
  </div>
);

/* ───────── "I have a topic" custom input ───────── */

const CustomTitleInput = ({ onIdentified }) => {
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const identify = async () => {
    if (!title.trim() || title.trim().length < 5) {
      toast.error('Tell us a bit more about your topic.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/blog-post/identify-keyword`, { title });
      onIdentified({ topic: title, ...data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Keyword identification failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="blog-custom-title">
      <Kicker lime>I have a topic</Kicker>
      <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(24px, 3.4vw, 32px)', letterSpacing: '-0.025em', color: C.text, lineHeight: 1.1, marginBottom: 18 }}>
        What&apos;s your topic or working title?
      </h2>
      <input
        type="text" value={title} onChange={(e) => setTitle(e.target.value)}
        data-testid="blog-custom-title-input"
        placeholder='e.g. "How to get your first 100 customers"'
        style={{
          width: '100%', background: C.surface, border: `1px solid ${C.border}`,
          color: C.text, padding: '16px 18px', borderRadius: 12, marginBottom: 14,
          fontFamily: MONO, fontSize: 15, outline: 'none',
        }}
      />
      <p style={{ fontFamily: MONO, fontSize: 11, color: C.faint, marginBottom: 22 }}>
        Hint: be specific. &quot;The drop calendar that actually sells&quot; beats &quot;Marketing tips&quot;.
      </p>
      <button onClick={identify} disabled={loading} data-testid="blog-identify-keyword-btn"
        style={{
          background: C.lime, color: C.limeText, border: 'none',
          cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1,
          padding: '14px 28px', borderRadius: 10,
          fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>
        {loading ? 'Finding keyword…' : 'Find the best keyword →'}
      </button>
    </div>
  );
};

/* ───────── Screen 2 — Generation progress ───────── */

const STEPS = [
  'Researching your audience',
  'Analysing search intent',
  'Structuring for readability',
  'Writing in your brand voice',
  'Optimising for search',
  'Done',
];

const GenerationProgress = ({ done }) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (done) { setTick(STEPS.length); return; }
    const t = setInterval(() => setTick((n) => Math.min(STEPS.length - 1, n + 1)), 1600);
    return () => clearInterval(t);
  }, [done]);
  return (
    <div data-testid="blog-generation-progress" style={{ maxWidth: 520, margin: '0 auto', textAlign: 'center', padding: '60px 0' }}>
      <Kicker lime>Working on your post</Kicker>
      <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(28px, 4vw, 40px)', letterSpacing: '-0.025em', color: C.text, lineHeight: 1.1, marginBottom: 40 }}>
        Writing your post…
      </h2>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, textAlign: 'left' }}>
        {STEPS.map((s, i) => {
          const isDone = i < tick;
          const isCurrent = i === tick && !done;
          return (
            <li key={i} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '11px 14px', marginBottom: 6, borderRadius: 8,
              background: isCurrent ? 'rgba(200,241,53,0.05)' : 'transparent',
              transition: 'background 0.3s, opacity 0.3s',
              opacity: isDone || isCurrent ? 1 : 0.4,
            }}>
              <span style={{
                width: 18, height: 18, borderRadius: '50%',
                background: isDone ? C.lime : 'transparent',
                border: isDone ? 'none' : `1.5px solid ${isCurrent ? C.lime : C.borderHi}`,
                display: 'grid', placeItems: 'center', flexShrink: 0,
              }}>
                {isDone && <Check size={11} weight="bold" color={C.limeText} />}
                {isCurrent && <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.lime, animation: 'sclBlogPulse 1s ease-in-out infinite' }} />}
              </span>
              <span style={{ fontFamily: MONO, fontSize: 13.5, color: isDone || isCurrent ? C.text : C.faint }}>{s}</span>
            </li>
          );
        })}
      </ul>
      <style>{`@keyframes sclBlogPulse { 0%,100% { opacity: 0.4 } 50% { opacity: 1 } }`}</style>
    </div>
  );
};

/* ───────── Screen 3 — Post output ───────── */

const ScoreBadge = ({ score }) => {
  const color = score === 'Strong' ? '#7be0ad' : score === 'Good' ? '#fcd34d' : '#f87171';
  return (
    <span data-testid="blog-seo-score-badge" style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 4,
      background: `${color}1a`, border: `1px solid ${color}55`,
      fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
      color: color,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
      SEO: {score}
    </span>
  );
};

/* Body renderer — markdown with ## H2s */
const PostBody = ({ md }) => {
  const blocks = useMemo(() => {
    if (!md) return [];
    return md.split(/\n{2,}/).map((b, i) => {
      const trimmed = b.trim();
      if (trimmed.startsWith('## ')) {
        return { type: 'h2', text: trimmed.replace(/^## /, ''), key: i };
      }
      return { type: 'p', text: trimmed, key: i };
    });
  }, [md]);
  return (
    <div data-testid="blog-post-body" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {blocks.map((b) => b.type === 'h2' ? (
        <h2 key={b.key} style={{
          fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(20px, 2.5vw, 25px)',
          letterSpacing: '-0.018em', color: C.text, lineHeight: 1.2, marginTop: 12, marginBottom: -6,
        }}>{b.text}</h2>
      ) : (
        <p key={b.key} style={{
          fontFamily: "'Lora', Georgia, serif", fontSize: 16.5, lineHeight: 1.78,
          color: 'rgba(240,240,232,0.85)', letterSpacing: '0.001em',
        }}>{b.text}</p>
      ))}
    </div>
  );
};

const SeoDetailsPanel = ({ post, onClose, onChange, onRecompute }) => {
  const [edit, setEdit] = useState({
    title: post.title, slug: post.slug, target_keyword: post.target_keyword,
    meta_description: post.meta_description, body_md: post.body_md,
  });
  const [savedScore, setSavedScore] = useState({ score: post.seo_score, checks: post.seo_checks || {} });
  const [recomputing, setRecomputing] = useState(false);

  const recompute = useCallback(async () => {
    setRecomputing(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/blog-post/recompute-score`, {
        title: edit.title, target_keyword: edit.target_keyword,
        meta_description: edit.meta_description, body_md: edit.body_md,
      });
      setSavedScore({ score: data.seo_score, checks: data.seo_checks });
      if (onRecompute) onRecompute({ score: data.seo_score, checks: data.seo_checks });
    } catch { /* silent */ }
    setRecomputing(false);
  }, [edit, onRecompute]);

  useEffect(() => { recompute(); }, [recompute]);

  const Row = ({ label, value, check, hint }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr auto', alignItems: 'start', gap: 14, padding: '14px 0', borderBottom: `1px solid ${C.border}` }}>
      <span style={{ fontFamily: MONO, fontSize: 10.5, color: C.faint, letterSpacing: '0.12em', textTransform: 'uppercase', paddingTop: 4 }}>{label}</span>
      <div>
        {value}
        {hint && <p style={{ fontFamily: MONO, fontSize: 10.5, color: C.faint, marginTop: 6 }}>{hint}</p>}
      </div>
      <span style={{ paddingTop: 6 }}>
        {check === true && <Check size={14} weight="bold" color="#7be0ad" />}
        {check === false && <span style={{ fontFamily: MONO, fontSize: 10, color: '#f87171', fontWeight: 700 }}>!</span>}
      </span>
    </div>
  );

  const update = (field, val) => {
    const next = { ...edit, [field]: val };
    setEdit(next);
    onChange(next);
  };

  return (
    <div data-testid="blog-seo-details-panel" style={{
      position: 'fixed', right: 0, top: 0, bottom: 0, width: 'min(520px, 100vw)',
      background: C.surface, borderLeft: `1px solid ${C.border}`,
      padding: 'clamp(24px, 4vw, 36px)', overflowY: 'auto', zIndex: 100,
      boxShadow: '-20px 0 50px rgba(0,0,0,0.4)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <Kicker lime>SEO Details</Kicker>
        <button onClick={onClose} data-testid="blog-seo-panel-close" style={{ background: 'transparent', border: 'none', color: C.dim, cursor: 'pointer', fontFamily: MONO, fontSize: 12 }}>Close ✕</button>
      </div>

      <div style={{
        padding: '14px 16px', borderRadius: 10, marginBottom: 18,
        background: savedScore.score === 'Strong' ? 'rgba(123,224,173,0.08)' : savedScore.score === 'Good' ? 'rgba(252,211,77,0.06)' : 'rgba(248,113,113,0.06)',
        border: `1px solid ${savedScore.score === 'Strong' ? 'rgba(123,224,173,0.32)' : savedScore.score === 'Good' ? 'rgba(252,211,77,0.32)' : 'rgba(248,113,113,0.32)'}`,
      }}>
        <p style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.18em', color: C.faint, textTransform: 'uppercase', marginBottom: 6 }}>SEO Score</p>
        <p style={{ fontFamily: DISP, fontSize: 22, fontWeight: 800, color: C.text, letterSpacing: '-0.015em' }}>
          {savedScore.score}
          {recomputing && <span style={{ fontFamily: MONO, fontSize: 11, color: C.faint, marginLeft: 10 }}>Recomputing…</span>}
        </p>
      </div>

      <Row label="Title tag" value={
        <input type="text" value={edit.title} onChange={(e) => update('title', e.target.value)} data-testid="seo-title-input"
          style={{ width: '100%', background: 'transparent', border: 'none', color: C.text, fontFamily: MONO, fontSize: 13, padding: 0, outline: 'none' }} />
      } check={savedScore.checks?.title_length_ok} hint={`${edit.title.length} / 60`} />

      <Row label="Meta description" value={
        <textarea value={edit.meta_description} onChange={(e) => update('meta_description', e.target.value)} data-testid="seo-meta-input"
          rows={3}
          style={{ width: '100%', background: 'transparent', border: 'none', color: C.text, fontFamily: MONO, fontSize: 13, padding: 0, outline: 'none', resize: 'vertical' }} />
      } check={savedScore.checks?.meta_length_ok} hint={`${edit.meta_description.length} / 155`} />

      <Row label="Target keyword" value={
        <input type="text" value={edit.target_keyword} onChange={(e) => update('target_keyword', e.target.value)} data-testid="seo-keyword-input"
          style={{ width: '100%', background: 'transparent', border: 'none', color: C.text, fontFamily: MONO, fontSize: 13, fontWeight: 700, padding: 0, outline: 'none' }} />
      } check={savedScore.checks?.keyword_in_title && savedScore.checks?.keyword_in_opening} hint={`Used naturally in title, opening${savedScore.checks?.keyword_in_h2 ? ', and one H2' : ''}`} />

      <Row label="URL slug" value={
        <input type="text" value={edit.slug || ''} onChange={(e) => update('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-'))} data-testid="seo-slug-input"
          style={{ width: '100%', background: 'transparent', border: 'none', color: C.text, fontFamily: MONO, fontSize: 13, padding: 0, outline: 'none' }} />
      } check={!!edit.slug} hint="lowercase, hyphenated" />

      <Row label="Word count" value={
        <span style={{ fontFamily: MONO, fontSize: 13, color: C.text }}>{savedScore.checks?.word_count || 0}</span>
      } check={savedScore.checks?.word_count_ok} hint="650+ words is the floor" />

      <Row label="Reading level" value={
        <span style={{ fontFamily: MONO, fontSize: 13, color: C.text }}>Grade {savedScore.checks?.reading_level || '—'}</span>
      } check={savedScore.checks?.reading_level && savedScore.checks.reading_level >= 7 && savedScore.checks.reading_level <= 11} hint="Clear and direct" />

      <Row label="Subheadings (H2)" value={
        <span style={{ fontFamily: MONO, fontSize: 13, color: C.text }}>{savedScore.checks?.h2_count || 0}</span>
      } check={(savedScore.checks?.h2_count || 0) >= 3} />

      <button onClick={recompute} data-testid="seo-recompute-btn"
        style={{
          marginTop: 22, background: 'transparent', color: C.lime,
          border: `1px solid ${C.borderLime}`, cursor: 'pointer',
          padding: '10px 16px', borderRadius: 8, width: '100%',
          fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>
        <ArrowsClockwise size={12} weight="bold" style={{ display: 'inline-block', marginRight: 6, verticalAlign: '-2px' }} />
        Recompute score
      </button>
    </div>
  );
};

/* Output view */

const PostOutput = ({ post, onPostUpdate, onBack }) => {
  const [showSeo, setShowSeo] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ ...post });
  const [busy, setBusy] = useState(false);

  const navigate = useNavigate();

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/blog-post/save`, {
        post_id: post.id,
        title: draft.title,
        slug: draft.slug,
        target_keyword: draft.target_keyword,
        meta_description: draft.meta_description,
        body_md: draft.body_md,
        cta_sentence: draft.cta_sentence || '',
      });
      onPostUpdate(data.post);
      toast.success('Saved');
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const regenerate = async () => {
    if (!window.confirm('Rewrite this post with the same topic? Costs 1 credit.')) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/blog-post/regenerate`, { post_id: post.id });
      onPostUpdate(data.post);
      setDraft({ ...data.post });
      toast.success('Rewritten');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Regeneration failed');
    } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/blog-post/${post.id}/publish`);
      onPostUpdate(data.post);
      toast.success('Published — your post is live.');
      // Open the live URL in a new tab so the founder sees the page immediately
      if (data.public_url) window.open(data.public_url, '_blank', 'noopener');
    } catch (e) {
      const detail = e.response?.data?.detail || 'Publish failed';
      if (typeof detail === 'string' && detail.toLowerCase().includes('public blog is not enabled')) {
        toast.error(detail, {
          action: { label: 'Open Settings', onClick: () => navigate('/dashboard/settings#public-blog') },
        });
      } else {
        toast.error(detail);
      }
    } finally { setBusy(false); }
  };

  const unpublish = async () => {
    if (!window.confirm('Take this post offline? The URL will return 404.')) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/blog-post/${post.id}/unpublish`);
      onPostUpdate(data.post);
      toast.success('Unpublished');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unpublish failed');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/blog-post/${post.id}`);
      toast.success('Deleted');
      navigate('/dashboard/assets?tab=blog');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="blog-post-output">
      <button onClick={onBack} data-testid="blog-back-btn"
        style={{ background: 'transparent', border: 'none', color: C.dim, cursor: 'pointer',
          fontFamily: MONO, fontSize: 11, letterSpacing: '0.08em', display: 'inline-flex',
          alignItems: 'center', gap: 6, marginBottom: 22 }}>
        <ArrowLeft size={13} /> Back
      </button>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <ScoreBadge score={post.seo_score} />
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.faint, letterSpacing: '0.16em', textTransform: 'uppercase' }}>{post.estimated_read_minutes} min read</span>
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.faint, letterSpacing: '0.16em', textTransform: 'uppercase' }}>{post.seo_checks?.word_count || '—'} words</span>
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.faint, letterSpacing: '0.16em', textTransform: 'uppercase' }}>Status: <span style={{ color: C.text }}>{post.status}</span></span>
      </div>

      {editing ? (
        <input type="text" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          data-testid="blog-edit-title-input"
          style={{ width: '100%', background: 'transparent', border: `1px solid ${C.borderLime}`,
            borderRadius: 8, padding: '12px 14px', marginBottom: 18,
            fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(26px, 4vw, 38px)', letterSpacing: '-0.025em',
            color: C.text, lineHeight: 1.1, outline: 'none' }} />
      ) : (
        <h1 data-testid="blog-post-title" style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(26px, 4vw, 38px)', letterSpacing: '-0.025em', color: C.text, lineHeight: 1.1, marginBottom: 18 }}>
          {post.title}
        </h1>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 30, paddingBottom: 22, borderBottom: `1px solid ${C.border}` }}>
        <div>
          <p style={{ fontFamily: MONO, fontSize: 9, color: C.faint, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 4 }}>Target keyword</p>
          <span data-testid="blog-target-keyword" style={{
            display: 'inline-block', padding: '4px 10px', borderRadius: 4, background: 'rgba(200,241,53,0.1)',
            border: `1px solid ${C.borderLime}`, color: C.lime,
            fontFamily: MONO, fontSize: 11, fontWeight: 700,
          }}>{post.target_keyword}</span>
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <p style={{ fontFamily: MONO, fontSize: 9, color: C.faint, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 4 }}>Suggested URL</p>
          <p style={{ fontFamily: MONO, fontSize: 11.5, color: C.dim, wordBreak: 'break-all' }}>scalitos.com/brands/yourbrand/{post.slug}</p>
        </div>
      </div>

      {editing ? (
        <textarea
          value={draft.body_md}
          onChange={(e) => setDraft({ ...draft, body_md: e.target.value })}
          data-testid="blog-edit-body-textarea"
          rows={24}
          style={{
            width: '100%', background: C.surface, border: `1px solid ${C.borderLime}`,
            color: C.text, padding: '18px 20px', borderRadius: 12,
            fontFamily: "'Lora', Georgia, serif", fontSize: 16, lineHeight: 1.78,
            outline: 'none', resize: 'vertical',
          }}
        />
      ) : (
        <PostBody md={post.body_md} />
      )}

      {/* Actions bar */}
      <div style={{
        position: 'sticky', bottom: 0,
        marginTop: 36, padding: '16px 0',
        background: 'linear-gradient(180deg, transparent 0%, #0a0a0a 30%)',
        display: 'flex', flexWrap: 'wrap', gap: 10,
      }}>
        {editing ? (
          <>
            <button onClick={save} disabled={busy} data-testid="blog-save-btn"
              style={{ background: C.lime, color: C.limeText, border: 'none', cursor: busy ? 'wait' : 'pointer',
                padding: '12px 22px', borderRadius: 10,
                fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
              }}>
              <FloppyDisk size={13} weight="bold" style={{ display: 'inline-block', marginRight: 6, verticalAlign: '-2px' }} />
              Save
            </button>
            <button onClick={() => { setEditing(false); setDraft({ ...post }); }}
              style={{ background: 'transparent', color: C.dim, border: `1px solid ${C.borderHi}`, cursor: 'pointer',
                padding: '12px 22px', borderRadius: 10,
                fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
              }}>Cancel</button>
          </>
        ) : (
          <>
            <button onClick={() => setEditing(true)} data-testid="blog-edit-btn"
              style={{ background: 'transparent', color: C.text, border: `1px solid ${C.borderHi}`, cursor: 'pointer',
                padding: '12px 22px', borderRadius: 10,
                fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
              }}>
              <Pencil size={13} weight="bold" style={{ display: 'inline-block', marginRight: 6, verticalAlign: '-2px' }} />
              Edit
            </button>
            <button onClick={() => setShowSeo(true)} data-testid="blog-seo-details-btn"
              style={{ background: 'transparent', color: C.text, border: `1px solid ${C.borderHi}`, cursor: 'pointer',
                padding: '12px 22px', borderRadius: 10,
                fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
              }}>
              <ListNumbers size={13} weight="bold" style={{ display: 'inline-block', marginRight: 6, verticalAlign: '-2px' }} />
              SEO Details
            </button>
            <button onClick={regenerate} disabled={busy} data-testid="blog-regenerate-btn"
              style={{ background: 'transparent', color: C.text, border: `1px solid ${C.borderHi}`, cursor: busy ? 'wait' : 'pointer',
                padding: '12px 22px', borderRadius: 10,
                fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
              }}>
              <ArrowsClockwise size={13} weight="bold" style={{ display: 'inline-block', marginRight: 6, verticalAlign: '-2px' }} />
              Rewrite (1 cr)
            </button>
            <span style={{ flex: 1 }} />
            {post.status === 'published' ? (
              <>
                <a href={post.public_url} target="_blank" rel="noopener noreferrer" data-testid="blog-view-live-link"
                  style={{ background: 'transparent', color: C.lime, border: `1px solid ${C.borderLime}`, cursor: 'pointer',
                    padding: '12px 22px', borderRadius: 10, textDecoration: 'none',
                    fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}>
                  <Eye size={13} weight="bold" />
                  View live →
                </a>
                <button onClick={unpublish} disabled={busy} data-testid="blog-unpublish-btn"
                  style={{ background: 'transparent', color: '#f87171', border: '1px solid rgba(248,113,113,0.32)', cursor: busy ? 'wait' : 'pointer',
                    padding: '12px 22px', borderRadius: 10,
                    fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                  }}>Unpublish</button>
              </>
            ) : (
              <button onClick={publish} disabled={busy} data-testid="blog-publish-btn"
                style={{ background: C.lime, color: C.limeText, border: 'none', cursor: busy ? 'wait' : 'pointer',
                  padding: '12px 22px', borderRadius: 10,
                  fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                }}>
                <Eye size={13} weight="bold" style={{ display: 'inline-block', marginRight: 6, verticalAlign: '-2px' }} />
                {busy ? 'Publishing…' : 'Publish →'}
              </button>
            )}
            <button onClick={remove} data-testid="blog-delete-btn"
              style={{ background: 'transparent', color: '#f87171', border: `1px solid rgba(248,113,113,0.32)`, cursor: 'pointer',
                padding: '12px 14px', borderRadius: 10,
                fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
              }}>
              <Trash size={13} weight="bold" />
            </button>
          </>
        )}
      </div>

      {showSeo && (
        <SeoDetailsPanel
          post={post}
          onClose={() => setShowSeo(false)}
          onChange={(next) => setDraft({ ...draft, ...next })}
          onRecompute={({ score, checks }) => onPostUpdate({ ...post, seo_score: score, seo_checks: checks })}
        />
      )}
    </div>
  );
};

/* ───────── Page wrapper ───────── */

export default function BlogPostCreatorPage() {
  // Phases: 'select' | 'topic-confirm' | 'custom-title' | 'topic-confirm-custom' | 'competitor-pick' | 'generating' | 'output'
  const [phase, setPhase] = useState('select');
  const [topicData, setTopicData] = useState(null); // {topic, keyword, search_volume, why_this_topic, alternates}
  const [competitorTopics, setCompetitorTopics] = useState(null); // {topics: [...]}
  const [post, setPost] = useState(null);
  const [generationDone, setGenerationDone] = useState(false);
  const [loading, setLoading] = useState(false);

  // Deep-link: ?id=<post_id> opens the existing post directly in output view
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const postId = searchParams.get('id');
    if (!postId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/blog-post/${postId}`);
        setPost(data.post);
        setPhase('output');
      } catch { /* fall through to select */ }
    })();
  }, [searchParams]);

  // Auto mode — run research immediately
  const pickAuto = async () => {
    setLoading(true);
    setPhase('topic-confirm');
    try {
      const { data } = await axios.post(`${API_URL}/api/blog-post/research-topic`, {});
      setTopicData({
        topic: data.topic, keyword: data.keyword,
        search_volume: data.search_volume, why: data.why_this_topic,
        alternates: data.alternates || [],
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Topic research failed');
      setPhase('select');
    } finally { setLoading(false); }
  };

  // Competitor mode — pull topics
  const pickCompetitor = async () => {
    setLoading(true);
    setPhase('competitor-pick');
    try {
      const { data } = await axios.post(`${API_URL}/api/blog-post/competitor-topics`, {});
      setCompetitorTopics(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Competitor topic discovery failed');
      setPhase('select');
    } finally { setLoading(false); }
  };

  // Custom title mode — show input
  const pickCustom = () => setPhase('custom-title');

  // After identifying keyword from custom title
  const onCustomIdentified = ({ topic, keyword, search_volume, rationale }) => {
    setTopicData({ topic, keyword, search_volume, why: rationale, alternates: [] });
    setPhase('topic-confirm-custom');
  };

  // After topic-confirm step → generate
  const generate = async (topicToUse, keywordToUse) => {
    setGenerationDone(false);
    setPhase('generating');
    try {
      const { data } = await axios.post(`${API_URL}/api/blog-post/generate`, {
        topic: topicToUse, keyword: keywordToUse,
      });
      setPost(data.post);
      setGenerationDone(true);
      setTimeout(() => setPhase('output'), 900);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Post generation failed';
      const isCredit = e.response?.status === 402;
      toast.error(typeof msg === 'string' ? msg : (msg.message || 'Failed'));
      if (isCredit) setPhase('select');
      else setPhase('topic-confirm');
    }
  };

  return (
    <DashboardLayout>
      <div data-testid="blog-post-creator" style={{
        background: C.bg, color: C.text, minHeight: '100vh',
        padding: 'clamp(36px, 5vw, 56px) clamp(20px, 4vw, 40px)',
      }}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>

          {phase === 'select' && (
            <TopicSelector
              onPickModeAuto={pickAuto}
              onPickModeTitle={pickCustom}
              onPickModeCompetitor={pickCompetitor}
            />
          )}

          {phase === 'topic-confirm' && (
            loading ? (
              <div data-testid="blog-finding-topic" style={{ textAlign: 'center', padding: '80px 0' }}>
                <Kicker lime>Researching</Kicker>
                <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(24px, 3.4vw, 32px)', letterSpacing: '-0.025em', color: C.text, lineHeight: 1.1, marginBottom: 18 }}>
                  Finding your best topic…
                </h2>
                <p style={{ fontFamily: MONO, fontSize: 13, color: C.dim }}>About 5 seconds.</p>
              </div>
            ) : topicData && (
              <TopicConfirm
                topic={topicData.topic} keyword={topicData.keyword}
                volume={topicData.search_volume} why={topicData.why}
                alternates={topicData.alternates}
                onConfirm={() => generate(topicData.topic, topicData.keyword)}
                onPickAlternate={(alt) => generate(alt.topic, alt.keyword)}
                onTryAgain={pickAuto}
              />
            )
          )}

          {phase === 'custom-title' && (
            <CustomTitleInput onIdentified={onCustomIdentified} />
          )}

          {phase === 'topic-confirm-custom' && topicData && (
            <TopicConfirm
              topic={topicData.topic} keyword={topicData.keyword}
              volume={topicData.search_volume} why={topicData.why}
              alternates={[]}
              onConfirm={() => generate(topicData.topic, topicData.keyword)}
              onPickAlternate={() => {}}
              onTryAgain={() => setPhase('custom-title')}
            />
          )}

          {phase === 'competitor-pick' && (
            loading ? (
              <div data-testid="blog-finding-competitor-topics" style={{ textAlign: 'center', padding: '80px 0' }}>
                <Kicker lime>Watching competitors</Kicker>
                <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(24px, 3.4vw, 32px)', letterSpacing: '-0.025em', color: C.text, lineHeight: 1.1, marginBottom: 18 }}>
                  Finding topics they cover that you don&apos;t…
                </h2>
              </div>
            ) : competitorTopics && (
              <div data-testid="blog-competitor-topics">
                <Kicker lime>Topics worth covering</Kicker>
                <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(24px, 3.4vw, 32px)', letterSpacing: '-0.025em', color: C.text, lineHeight: 1.1, marginBottom: 12 }}>
                  Pick one to write about.
                </h2>
                <p style={{ fontFamily: MONO, fontSize: 12, color: C.dim, marginBottom: 24 }}>
                  {competitorTopics.source === 'industry_defaults'
                    ? 'No competitors tracked yet — these are common winners in your industry.'
                    : `Pulled from: ${(competitorTopics.competitors_used || []).join(', ')}`}
                </p>
                <div style={{ display: 'grid', gap: 12 }}>
                  {(competitorTopics.topics || []).map((t, i) => (
                    <button key={i} onClick={() => generate(t.topic, t.keyword)} data-testid={`blog-competitor-topic-${i}`}
                      style={{ textAlign: 'left', background: C.surface, border: `1px solid ${C.border}`,
                        borderRadius: 14, padding: 'clamp(18px, 2.5vw, 24px)', cursor: 'pointer',
                        transition: 'border-color 0.2s, background 0.2s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.borderLime; e.currentTarget.style.background = '#181818'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.surface; }}>
                      <p style={{ fontFamily: DISP, fontWeight: 700, fontSize: 17, color: C.text, marginBottom: 8 }}>{t.topic}</p>
                      <p style={{ fontFamily: MONO, fontSize: 11, color: C.dim, marginBottom: 8 }}>
                        Target: <span style={{ color: C.text }}>{t.keyword}</span> · {t.search_volume || 'medium'} volume
                      </p>
                      {t.why_steal && <p style={{ fontFamily: MONO, fontSize: 11, color: C.faint, lineHeight: 1.7 }}>{t.why_steal}</p>}
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 12, fontFamily: MONO, fontSize: 10, color: C.lime, letterSpacing: '0.08em', fontWeight: 700 }}>
                        Write this <CaretRight size={11} weight="bold" />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )
          )}

          {phase === 'generating' && (
            <GenerationProgress done={generationDone} />
          )}

          {phase === 'output' && post && (
            <PostOutput
              post={post}
              onPostUpdate={(updated) => setPost(updated)}
              onBack={() => setPhase('select')}
            />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
